


import InstagramIcon from '@mui/icons-material/Instagram'; // Import the Instagram icon from MUI
import { pink } from '@mui/material/colors'; // Import the pink color from MUI for customization
import axios from "axios"; // Import Axios for API calls
import Papa from "papaparse"; // Import PapaParse to parse CSV
import React, { useState } from "react";
import Skeleton from 'react-loading-skeleton'; // Import Skeleton loader
import 'react-loading-skeleton/dist/skeleton.css'; // Import Skeleton CSS
import Navbar from './Navbar'; // Import the Navbar

const ReelsFetcher = () => {
  const [urls, setUrls] = useState([]);
  const [inputUrl, setInputUrl] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [csvFile, setCsvFile] = useState(null); // To store uploaded CSV file
  const [loading, setLoading] = useState(false); // Track loading state

  // Handle the input change
  const handleInputChange = (e) => {
    setInputUrl(e.target.value);
  };

  // Handle adding new URL and fetching data when user presses "Enter"
  const handleKeyPress = async (e) => {
    if (e.key === "Enter" && inputUrl) {
      const newUrls = [...urls, inputUrl];
      setUrls(newUrls);
      setInputUrl(""); // Clear the input field after adding the URL

      // Prepare the data to send to the API
      const requestData = { urls: newUrls };
      fetchReelsData(requestData); // Fetch the data
    }
  };

  // Handle removing a URL
  const handleRemoveUrl = async (urlToRemove) => {
    const updatedUrls = urls.filter(url => url !== urlToRemove);
    setUrls(updatedUrls);

    // If URLs are left, automatically trigger fetch
    if (updatedUrls.length > 0) {
      const requestData = { urls: updatedUrls };
      fetchReelsData(requestData); // Fetch data
    } else {
      setData(null);
      setError("No URLs left to fetch data.");
    }
  };

  // Handle CSV file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "text/csv") {
      setCsvFile(file);
      parseCSV(file); // Parse CSV when uploaded
    } else {
      setError("Please upload a valid CSV file.");
    }
  };

  // Parse CSV to extract Instagram URLs
  const parseCSV = (file) => {
    Papa.parse(file, {
      complete: (result) => {
        // Extract the first column as URLs
        const extractedUrls = result.data.map((row) => row[0]).filter((url) => url);
        if (extractedUrls.length > 0) {
          const newUrls = [...urls, ...extractedUrls];
          setUrls(newUrls); // Store extracted URLs
          fetchReelsData({ urls: newUrls }); // Fetch Instagram data for the URLs
        } else {
          setError("No valid URLs found in the CSV.");
        }
      },
      error: (error) => {
        setError("Error parsing CSV: " + error.message);
      },
    });
  };

  // Fetch Instagram data for all URLs
  const fetchReelsData = async (requestData) => {
    setLoading(true); // Start loading when the fetch begins
    try {
      const response = await axios.post("https://server-side-influencer.onrender.com/getInstagramData", requestData);
      setData(response.data);
      setError(null);
    } catch (err) {
      setError("An error occurred while fetching the data.");
      setData(null);
    } finally {
      setLoading(false); // End loading when the fetch is complete
    }
  };

  return (
    <div className="flex h-screen mb-0">
      <div className="text-white fixed top-0 left-0 bottom-0 z-10">
        <Navbar />
      </div>

      {/* Main Contents */}
      <div className="flex-1 p-4" style={{ marginLeft: '70px' }}>
        {/* Title Above the Search Bars with Instagram Icon */}
        <div className="flex justify-center items-center mb-6">
          <InstagramIcon style={{ fontSize: 40, color: pink[500] }} /> {/* Instagram Icon with Pink Color */}
          <h1 className="text-3xl font-semibold ml-2">Instagram Reels URL Checker</h1> {/* Title next to the icon */}
        </div>

        {/* Search bar */}
        <div className="p-4 bg-white flex justify-center items-center space-x-2">
          <input
            type="text"
            value={inputUrl}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress} // Trigger handleKeyPress when Enter key is pressed
            className="p-2 w-72 border border-gray-300 rounded text-sm"
            placeholder="Enter Instagram Reel URL"
          />
        </div>

        {/* CSV File Upload */}
        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-900">Upload CSV of Instagram Reels URLs (Optional)</label>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Display entered URLs below the search bar with cross marks */}
        {urls.length > 0 && (
          <div className="mt-4">
            <h3 className="text-xl font-semibold mb-2">Entered URLs:</h3>
            <div className="flex flex-wrap space-x-2">
              {urls.map((url, index) => (
                <div key={index} className="flex items-center bg-gray-200 rounded p-2 mb-2">
                  <p className="text-sm">{url}</p>
                  <button
                    onClick={() => handleRemoveUrl(url)}
                    className="ml-2 text-red-500 font-bold"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error message */}
        {error && <p className="text-red-500 mt-2 text-center">{error}</p>}

        {/* Display the fetched data */}
        {loading ? (
          // Display Skeleton Loader while data is loadings
          <SkeletonTable />
        ) : data ? (
          <div className="mt-6 px-4 overflow-x-auto">
            {/* Title for Average Statistics */}
            <h2 className="text-2xl font-semibold mb-6 text-center mt-10">Average Statistics</h2>

            {/* Cards for average stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              <div className="bg-white p-4 rounded shadow-md text-center">
                <h3 className="text-xl font-semibold mb-2">Likes</h3>
                <p className="text-2xl text-green-500">{data.averages.likes}</p>
              </div>
              <div className="bg-white p-4 rounded shadow-md text-center">
                <h3 className="text-xl font-semibold mb-2">Comments</h3>
                <p className="text-2xl text-blue-500">{data.averages.comments}</p>
              </div>
              <div className="bg-white p-4 rounded shadow-md text-center">
                <h3 className="text-xl font-semibold mb-2">Views</h3>
                <p className="text-2xl text-gray-700">{data.averages.views}</p>
              </div>
            </div>

            {/* Table Layout with Tailwind CSS */}
            <div className="overflow-x-auto mt-6">
              <table className="min-w-full table-auto text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-2 text-left">User</th>
                    <th className="px-4 py-2 text-left">Video</th>
                    <th className="px-4 py-2 text-left">Likes</th>
                    <th className="px-4 py-2 text-left">Comments</th>
                    <th className="px-4 py-2 text-left">Views</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((result, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-2">
                        {result.user && result.user.full_name ? (
                          <>
                            <p className="font-semibold">{result.user.full_name}</p>
                            <p className="text-sm text-gray-600">@{result.user.username}</p>
                          </>
                        ) : (
                          <p className="text-sm text-gray-600">No user info</p>
                        )}
                      </td>

                      <td className="px-4 py-2">
                        <video
                          controls
                          className="w-24 h-24 object-cover rounded"
                          src={result.video_url}
                        />
                      </td>
                      <td className="px-4 py-2">{result.like_count}</td>
                      <td className="px-4 py-2">{result.comment_count}</td>
                      <td className="px-4 py-2">{result.play_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

// Skeleton Loader Component for Table
const SkeletonTable = () => (
  <div className="mt-6 px-4 overflow-x-auto">
    <Skeleton count={5} height={30} className="mb-4" />
    <Skeleton count={5} height={30} className="mb-4" />
    <Skeleton count={5} height={30} className="mb-4" />
  </div>
);

export default ReelsFetcher;
